// VERSION: v5 — adds Sash as ordered_window QBO Product (no cutlist needed)
import { db } from './firebase-init';
import {
  collection, addDoc, updateDoc, deleteDoc,
  doc, getDoc, getDocs, query, where, orderBy,
  serverTimestamp
} from 'firebase/firestore';

const ESTIMATES_COLLECTION = 'estimates';

export async function generateEstimateId() {
  const now  = new Date();
  const mm   = String(now.getMonth() + 1).padStart(2, '0');
  const dd   = String(now.getDate()).padStart(2, '0');
  const yyyy = now.getFullYear();
  const dateStr = `${mm}${dd}${yyyy}`;
  const prefix  = `EST-${dateStr}-`;

  const q    = query(
    collection(db, ESTIMATES_COLLECTION),
    where('estimateId', '>=', prefix),
    where('estimateId', '<',  prefix + '\uf8ff'),
  );
  const snap = await getDocs(q);

  let maxSeq = 0;
  snap.docs.forEach(d => {
    const eid = d.data().estimateId || '';
    const seq = parseInt(eid.replace(prefix, ''), 10);
    if (!isNaN(seq) && seq > maxSeq) maxSeq = seq;
  });

  const count = String(maxSeq + 1).padStart(3, '0');
  return `${prefix}${count}`;
}

export async function createEstimate(data) {
  const estimateId = await generateEstimateId();
  const ref = await addDoc(collection(db, ESTIMATES_COLLECTION), {
    ...data,
    estimateId,
    status: data.status || 'draft',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return { id: ref.id, estimateId };
}

export async function updateEstimate(id, data) {
  await updateDoc(doc(db, ESTIMATES_COLLECTION, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function getEstimate(id) {
  const snap = await getDoc(doc(db, ESTIMATES_COLLECTION, id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function getEstimates(customerId = null, userUid = null) {
  const constraints = [];

  if (customerId) constraints.push(where('customerId',  '==', customerId));
  if (userUid)    constraints.push(where('createdByUid', '==', userUid));

  constraints.push(orderBy('createdAt', 'desc'));

  const q = query(collection(db, ESTIMATES_COLLECTION), ...constraints);
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

export async function deleteEstimate(id) {
  await deleteDoc(doc(db, ESTIMATES_COLLECTION, id));
}

// Jobs in Production carry a contractNumber but no direct estimateId back to
// where they started — this is how we find the originating Estimate.
export async function getEstimateByContractNumber(contractNumber) {
  if (!contractNumber) return null;
  const q = query(collection(db, ESTIMATES_COLLECTION), where('contractNumber', '==', contractNumber));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Return the most recently updated one, same defensive pattern as getJobOrderByEstimate.
  docs.sort((a, b) => (b.updatedAt?.seconds || b.createdAt?.seconds || 0) - (a.updatedAt?.seconds || a.createdAt?.seconds || 0));
  return docs[0];
}

export const LINE_TYPES = [
  { value: 'twp_window',      label: 'TWP Window',      color: '#8B4513', bg: 'rgba(139,69,19,0.1)'  },
  { value: 'ordered_window',  label: 'Ordered Window',  color: '#2563eb', bg: 'rgba(37,99,235,0.1)'  },
  { value: 'door',            label: 'Door',             color: '#7c3aed', bg: 'rgba(124,58,237,0.1)' },
  { value: 'adder',           label: 'Adder',            color: '#059669', bg: 'rgba(5,150,105,0.1)'  },
  { value: 'note',            label: 'Note',             color: '#6b7280', bg: 'rgba(107,114,128,0.1)'},
];

export const WINDOW_TYPES = [
  'Double Hung', 'Horizontal Slider', '3-Lite Slider', 'Picture Window',
  'Casement', 'Basement Hopper', 'Brickmould',
];

export const TWP_WINDOW_SUBTYPES = [
  { value: '',             label: '— Select Type —'  },
  { value: 'dh',          label: 'Double Hung'       },
  { value: 'hs',          label: 'Horizontal Slider' },
  { value: 'threeLiteHS', label: '3-Light Slider'    },
  { value: 'pw',          label: 'Picture Window'    },
  { value: 'csmt',        label: 'Casement'          },
  { value: 'bsmt',        label: 'Basement Hopper'   },
  { value: 'bm',          label: 'Bay / Mullion'     },
];

export const DOOR_TYPES = [
  'Exterior Door', 'Patio Door', 'Storm Door', 'Side Lites Door',
];

export const ADDER_TYPES = [
  'Window Installation',
  'Inside Trim',
  'Outside Trim',
  'Trim Out',
  'Brickmold',
  'Coving',
  'Rework Opening',
  'Remove Whole Frame',
  'Remove Rotten Sills',
  'Trash Removal',
  'Screen Repair',
  'Glass Repair',
  'Repair',
  'Shipping / Delivery',
  'Fuel Surcharge',
  'Mileage',
  'Handling & Packaging',
  'Overnight Motel Stay',
  'Specialty Window',
  'Difficult Factor',
  'Late Fee',
  'Deduction',
  'Setup / Misc. Fee',
  'Service Call',
];

export const ORDERED_WINDOW_TYPES_LIST = [
  'Minnkota Window',
  'ProVia Window',
  'Vector Slider Window',
  'Sash',
];

// Strict QBO Product picker for ordered_window rows. Locked binding — once set
// on a line, description edits don't break QBO sync.
// "Sash" lives here too — sashes never go to the cutlist (measurements are
// straightforward), whether they're ordered from a supplier OR built in-shop.
export const ORDERED_PRODUCT_OPTIONS = [
  { value: '',          label: '— Pick QBO Product —' },
  { value: 'Minnkota',  label: 'Minnkota'             },
  { value: 'ProVia',    label: 'ProVia'               },
  { value: 'Vector',    label: 'Vector'               },
  { value: 'Sash',      label: 'Sash'                 },
  { value: 'Other',     label: 'Other / Custom'       },
];

export const DOOR_PRODUCT_OPTIONS = [
  { value: '',                label: '— Pick QBO Product —' },
  { value: 'Exterior Door',   label: 'Exterior Door'         },
  { value: 'Patio Door',      label: 'Patio Door'            },
  { value: 'Storm Door',      label: 'Storm Door'            },
  { value: 'Side Lites Door', label: 'Side Lites'            },
  { value: 'Other',           label: 'Other / Custom'        },
];

export const EMPTY_LINE = () => ({
  id:            Date.now() + Math.random(),
  type:          'twp_window',
  qty:           '',
  description:   '',
  width:         '',
  height:        '',
  room:          '',
  ui:            '',
  price:         '',
  windowSubType: '',
});

// ═══════════════════════════════════════════════════════════════════════════════
// QuickBooks Online Sync Mapping
// ═══════════════════════════════════════════════════════════════════════════════

export const QBO_COLOR_SUFFIX = {
  'White':              'WH',
  'Dk Woodgrain/White': 'WGDK',
};

export const QBO_ADDER_MAPPING = {
  'Window Installation':  'Labor:Labor-Install',
  'Inside Trim':          'Labor:Labor-Trim (Inside)',
  'Outside Trim':         'Labor- Trim (Outside)',
  'Trim Out':             'Trim Out',
  'Brickmold':            'Labor:Labor-Brickmold',
  'Coving':               'Labor:Labor-Coving',
  'Rework Opening':       'Labor:Labor-Rework Opening',
  'Remove Whole Frame':   'Labor:Labor- Remove Whole Frames',
  'Remove Rotten Sills':  'Remove Rotten Sills',
  'Trash Removal':        'Trash Removal',
  'Screen Repair':        'Screen Repair',
  'Glass Repair':         'Glass Repair',
  'Repair':               'Repair',
  'Shipping / Delivery':  'Shipping - Customer',
  'Fuel Surcharge':       'Mileage',
  'Mileage':              'Mileage',
  'Handling & Packaging': 'Handling and packaging',
  'Overnight Motel Stay': 'Overnight Motel Stay',
  'Specialty Window':     'Specialty Window',
  'Difficult Factor':     'Difficult Factor',
  'Late Fee':             'Late Fee',
  'Deduction':            'Deduction',
  'Setup / Misc. Fee':    'Labor-Set Up Fee',
  'Service Call':         'Service Call',
};

export const QBO_DOOR_MAPPING = {
  'Exterior Door':    'DOOREXT',
  'Patio Door':       'DOORPAT',
  'Storm Door':       'DOORSTO',
  'Side Lites Door':  'DOORSIDELITE',
};

export const QBO_ORDERED_WINDOW_MAPPING = {
  // Canonical (matches ORDERED_PRODUCT_OPTIONS values)
  'Minnkota':              'MINKOT',
  'ProVia':                'ProVia Window',
  'Vector':                'VECTORWIN',
  'Sash':                  'SASH',                  // auto-creates on first sync
  // Aliases — fuzzy fallback for legacy estimates without orderedProduct
  'Minnkota Window':       'MINKOT',
  'ProVia Window':         'ProVia Window',
  'Provia':                'ProVia Window',
  'Vector Window':         'VECTORWIN',
  'Vector Slider':         'VECTORWIN',
  'Vector Slider Window':  'VECTORWIN',
};

export const FRIENDLY_NAME_TO_SUBTYPE = {
  'Double Hung':        'dh',
  'Slider':             'hs',
  'Horizontal Slider':  'hs',
  '3 Lite Slider':      'threeLiteHS',
  '3-Lite Slider':      'threeLiteHS',
  '3-Light Slider':     'threeLiteHS',
  'Picture Window':     'pw',
  'Casement':           'csmt',
  'Awning':             'csmt',
  'Basement Hopper':    'bsmt',
  'Brickmould':         'bm',
  'Bay / Mullion':      'bm',
};

export const TWP_CODE_TO_SUBTYPE = {
  'DH':   'dh',
  'HS':   'hs',
  '3HS':  'threeLiteHS',
  'PW':   'pw',
  'CSMT': 'csmt',
  'BSMT': 'bsmt',
  'BM':   'bm',
};

/**
 * Detect which ordered window category a description matches. Auto-locks
 * orderedProduct on first input.
 */
export function detectOrderedProductFromDescription(desc) {
  if (!desc || typeof desc !== 'string') return null;
  const lower = desc.toLowerCase();
  if (lower.includes('minnkota') || lower.includes('minkota'))  return 'Minnkota';
  if (lower.includes('provia'))                                  return 'ProVia';
  if (lower.includes('vector'))                                  return 'Vector';
  if (lower.includes('sash'))                                    return 'Sash';
  return null;
}

/**
 * Detect which door category a description matches. Auto-locks doorProduct.
 */
export function detectDoorProductFromDescription(desc) {
  if (!desc || typeof desc !== 'string') return null;
  const lower = desc.toLowerCase();
  if (lower.includes('side lite') || lower.includes('sidelite') || lower.includes('side-lite')) return 'Side Lites Door';
  if (lower.includes('storm'))                                                                    return 'Storm Door';
  if (lower.includes('patio') || lower.includes('sliding') || lower.includes('slider door'))     return 'Patio Door';
  if (lower.includes('exterior') || lower.includes('entry') || lower.includes('front door')
      || lower.includes('back door') || lower.includes('garage door'))                            return 'Exterior Door';
  return null;
}

export function getTwpWindowQboName({ windowSubType, color, handing, config }) {
  const colorSuffix = QBO_COLOR_SUFFIX[color] || 'WH';
  const h = (handing || '').toLowerCase().trim();

  switch (windowSubType) {
    case 'dh':           return `DHWIN-${colorSuffix}`;
    case 'hs':           return `2LWIN-${colorSuffix}`;
    case 'threeLiteHS':  return `3LWIN-${colorSuffix}`;
    case 'pw':           return `PWWIN-${colorSuffix}`;
    case 'csmt':
      if (h === 'awning')                       return `AWCM-${colorSuffix}`;
      if (h === 'hinge right')                  return `SGCMR-${colorSuffix}`;
      if (h === 'fixed unit' || h === 'fixed')  return `SGCMFIX-${colorSuffix}`;
      if (config === '2-Lite')                  return `PWCM-${colorSuffix}`;
      if (config === '3-Lite')                  return `3LTCM-${colorSuffix}`;
      if (config === '4-Lite')                  return `4LTCM-${colorSuffix}`;
      if (config === '5-Lite')                  return `5LTCM-${colorSuffix}`;
      return `SGCML-${colorSuffix}`;
    case 'bsmt':
    case 'bm':           return null;
    case 'mulled':       return null;
    default:             return null;
  }
}

export function computeQboName(line) {
  if (!line || !line.type) return null;

  if (line.type === 'twp_window') {
    if (line.windowSubType === 'mulled' && line.cutlistWindowsJson) {
      try {
        const subs = JSON.parse(line.cutlistWindowsJson);
        if (Array.isArray(subs) && subs.length > 0) {
          const first = subs[0];
          const sub = FRIENDLY_NAME_TO_SUBTYPE[first.windowType]
                   || TWP_CODE_TO_SUBTYPE[first.type]
                   || null;
          let handing = first.handing;
          if (first.windowType === 'Awning') handing = 'Awning';
          else if (first.hingeSide === 'right') handing = 'Hinge Right';
          else if (first.hingeSide === 'left')  handing = 'Hinge Left';
          return getTwpWindowQboName({
            windowSubType: sub,
            color:         first.color || line.twpConfig?.variant || 'White',
            handing,
            config:        first.config || 'Single',
          });
        }
      } catch { /* fall through */ }
      return null;
    }
    const inferredAwning = line.twpConfig?.windowType === 'Awning' ? 'Awning' : null;
    return getTwpWindowQboName({
      windowSubType: line.windowSubType,
      color:         line.twpConfig?.variant || line.color,
      handing:       line.twpConfig?.handing || line.handing || inferredAwning,
      config:        line.twpConfig?.config  || line.config,
    });
  }

  if (line.type === 'door') {
    if (line.doorProduct) {
      if (line.doorProduct === 'Other') return null;
      const qbo = QBO_DOOR_MAPPING[line.doorProduct];
      if (qbo) return qbo;
    }
    return QBO_DOOR_MAPPING[line.description] || null;
  }

  if (line.type === 'adder') {
    return QBO_ADDER_MAPPING[line.description] || null;
  }

  if (line.type === 'ordered_window') {
    if (line.orderedProduct) {
      if (line.orderedProduct === 'Other') return null;
      const qbo = QBO_ORDERED_WINDOW_MAPPING[line.orderedProduct];
      if (qbo) return qbo;
    }
    const desc = line.description || '';
    if (QBO_ORDERED_WINDOW_MAPPING[desc]) return QBO_ORDERED_WINDOW_MAPPING[desc];
    const lower = desc.toLowerCase();
    for (const [key, qboName] of Object.entries(QBO_ORDERED_WINDOW_MAPPING)) {
      if (lower.includes(key.toLowerCase())) return qboName;
    }
    return null;
  }

  return null;
}

export function computeQboNameForJobOrderWindow(jw) {
  if (!jw) return null;
  const lt = jw.lineType || jw.type;

  if (lt === 'twp_window') {
    return getTwpWindowQboName({
      windowSubType: TWP_CODE_TO_SUBTYPE[jw.twpType] || null,
      color:         jw.colorInside || jw.colorOutside || 'White',
      handing:       jw.handing,
      config:        jw.twpConfig,
    });
  }

  if (lt === 'door') {
    if (jw.doorProduct) {
      if (jw.doorProduct === 'Other') return null;
      const qbo = QBO_DOOR_MAPPING[jw.doorProduct];
      if (qbo) return qbo;
    }
    return QBO_DOOR_MAPPING[jw.description] || QBO_DOOR_MAPPING[jw.style] || null;
  }

  if (lt === 'ordered_window') {
    if (jw.orderedProduct) {
      if (jw.orderedProduct === 'Other') return null;
      const qbo = QBO_ORDERED_WINDOW_MAPPING[jw.orderedProduct];
      if (qbo) return qbo;
    }
    const desc = jw.description || jw.style || '';
    if (QBO_ORDERED_WINDOW_MAPPING[desc]) return QBO_ORDERED_WINDOW_MAPPING[desc];
    const lower = desc.toLowerCase();
    for (const [key, qboName] of Object.entries(QBO_ORDERED_WINDOW_MAPPING)) {
      if (lower.includes(key.toLowerCase())) return qboName;
    }
    return null;
  }

  return null;
}

export function computeQboNameForAdder(adder) {
  if (!adder) return null;
  const name = adder.name || adder.description || '';
  return QBO_ADDER_MAPPING[name] || null;
}
